import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Eye, Home, LayoutGrid, FileBarChart, Users, Settings, ChevronDown, ChevronRight, Menu, X, LogOut, Sun, Moon, Globe, ArrowLeft, Lock, Timer } from "lucide-react";
import { useModules, iconOf } from "@/hooks/useModules";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";

const ROLE_LABEL = { super_admin: "Super Admin", admin: "Admin", user: "User" };

export const useTheme = () => {
  const [light, setLight] = useState(() => document.documentElement.classList.contains("light"));
  const toggle = () => {
    const next = !light;
    setLight(next);
    document.documentElement.classList.toggle("light", next);
    localStorage.setItem("mieruka_theme", next ? "light" : "dark");
  };
  return { light, toggle };
};

export const initials = (name) => (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

const Avatar = ({ name, size = "h-9 w-9" }) => (
  <div className={`${size} flex shrink-0 items-center justify-center rounded-full bg-thblue/15 font-mono-hud text-[11px] font-semibold text-thblue`}>{initials(name)}</div>
);

const NAV = [
  { to: "/", label: "Home", icon: Home, end: true },
  { to: "/reports", label: "Reports", icon: FileBarChart },
  { to: "/settings?section=users", label: "Team", icon: Users, admin: true, match: (p, s) => p === "/settings" && s.includes("section=users") },
  { to: "/settings", label: "Settings", icon: Settings, admin: true, match: (p, s) => p === "/settings" && !s.includes("section=users") },
];

const Sidebar = ({ compact, onNavigate }) => {
  const { user } = useAuth();
  const { pathname, search } = useLocation();
  const [dashOpen, setDashOpen] = useState(pathname.startsWith("/module") || pathname === "/aht");
  const { modules } = useModules();
  const isAdmin = ["admin", "super_admin"].includes(user?.role);

  const item = (active) => `group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${
    active ? "bg-thblue/15 text-thstrong" : "text-thmute hover:bg-thhover/5 hover:text-thstrong"}`;

  return (
    <div className="flex h-full flex-col">
      <Link to="/" data-testid="sidebar-logo" className="flex items-center gap-3 px-3 py-5" onClick={onNavigate}>
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-thblue shadow-[0_6px_18px_-6px_rgb(var(--th-blue)/0.7)]">
          <Eye className="h-5 w-5 text-white" strokeWidth={2.4} />
        </div>
        {!compact && <span className="font-heading text-xl font-extrabold tracking-tight text-thstrong">Mieruka</span>}
      </Link>

      <nav className="flex-1 space-y-1 px-2" data-testid="sidebar-nav">
        {NAV.slice(0, 1).map((n) => (
          <NavLink key={n.to} to={n.to} end data-testid="nav-home" onClick={onNavigate} className={({ isActive }) => item(isActive)}>
            <n.icon className="h-4 w-4 shrink-0" /> {!compact && n.label}
          </NavLink>
        ))}
        <button data-testid="nav-dashboards" onClick={() => setDashOpen(!dashOpen)} className={`${item(pathname.startsWith("/module") || pathname === "/aht")} w-full`}>
          <LayoutGrid className="h-4 w-4 shrink-0" />
          {!compact && <><span className="flex-1 text-left">Dashboards</span>{dashOpen ? <ChevronDown className="h-3.5 w-3.5 text-thdim" /> : <ChevronRight className="h-3.5 w-3.5 text-thdim" />}</>}
        </button>
        {dashOpen && !compact && (
          <div className="ml-4 space-y-0.5 border-l border-thborder/10 pl-3" data-testid="nav-dashboards-list">
            {(modules || []).map(({ slug, name, icon, allowed: ok }) => {
              const Icon = iconOf(icon);
              const active = pathname === `/module/${slug}`;
              return ok ? (
                <NavLink key={slug} to={`/module/${slug}`} data-testid={`nav-module-${slug}`} onClick={onNavigate}
                  className={`flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors ${active ? "text-thcyan" : "text-thmute hover:text-thstrong"}`}>
                  <Icon className="h-3.5 w-3.5" /> {name}
                </NavLink>
              ) : (
                <span key={slug} data-testid={`nav-module-${slug}-locked`} className="flex items-center gap-2.5 px-2.5 py-1.5 text-xs text-thfaint"><Lock className="h-3 w-3" /> {name}</span>
              );
            })}
            <NavLink to="/aht" data-testid="nav-module-aht" onClick={onNavigate}
              className={`flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors ${pathname === "/aht" ? "text-thcyan" : "text-thmute hover:text-thstrong"}`}>
              <Timer className="h-3.5 w-3.5" /> AHT Analytics
            </NavLink>
          </div>
        )}
        {NAV.slice(1).filter((n) => !n.admin || isAdmin).map((n) => {
          const active = n.match ? n.match(pathname, search) : pathname === n.to;
          return (
            <Link key={n.label} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`} onClick={onNavigate} className={item(active)}>
              <n.icon className="h-4 w-4 shrink-0" /> {!compact && n.label}
            </Link>
          );
        })}
      </nav>

      <div className="border-t border-thborder/10 p-3">
        <div className="flex items-center gap-3 rounded-xl px-2 py-2">
          <Avatar name={user?.name} />
          {!compact && (
            <div className="min-w-0">
              <p data-testid="sidebar-user-name" className="truncate text-sm font-semibold text-thstrong">{user?.name}</p>
              <p className="mk-sub">{ROLE_LABEL[user?.role]}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TenantPill = () => {
  const { tenants, tenantId, tenant, switchTenant, canSwitch } = useTenant() || {};
  const [open, setOpen] = useState(false);
  if (!canSwitch || !tenants?.length) return tenant ? <span className="mk-pill hidden sm:inline-flex"><Globe className="h-3.5 w-3.5 text-thcyan" />{tenant.name}</span> : null;
  return (
    <div className="relative">
      <button data-testid="tenant-switcher" onClick={() => setOpen(!open)} className="mk-pill">
        <Globe className="h-3.5 w-3.5 text-thcyan" />
        <span className="hidden max-w-[160px] truncate sm:inline">{tenant?.name || "Select client"}</span>
        <ChevronDown className="h-3.5 w-3.5 text-thdim" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="mk-card absolute right-0 z-20 mt-2 max-h-72 w-60 overflow-y-auto p-1.5">
            {tenants.map((t) => (
              <button key={t.id} data-testid={`tenant-option-${t.name}`} onClick={() => { switchTenant(t.id); setOpen(false); }}
                className={`w-full rounded-lg px-3 py-2 text-left text-xs transition-colors hover:bg-thhover/5 ${t.id === tenantId ? "font-bold text-thcyan" : "text-thbody"}`}>
                {t.name}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export const AppShell = ({ children, back = true, wide = false }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { light, toggle } = useTheme();
  const [drawer, setDrawer] = useState(false);
  const isAdmin = ["admin", "super_admin"].includes(user?.role);
  useEffect(() => { setDrawer(false); }, [pathname]);

  return (
    <div className="flex min-h-screen bg-appbg">
      <aside className="mk-shell-side sticky top-0 hidden h-screen w-[72px] shrink-0 border-r border-thborder/10 bg-thcard/60 md:block lg:w-60" data-testid="app-sidebar">
        <div className="hidden h-full lg:block"><Sidebar /></div>
        <div className="h-full lg:hidden"><Sidebar compact /></div>
      </aside>

      {drawer && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDrawer(false)} />
          <div className="mk-shell-side absolute inset-y-0 left-0 w-64 border-r border-thborder/10 bg-thsolid shadow-2xl" data-testid="app-sidebar-drawer">
            <button onClick={() => setDrawer(false)} className="absolute right-3 top-5 text-thmute" aria-label="Close menu"><X className="h-5 w-5" /></button>
            <Sidebar onNavigate={() => setDrawer(false)} />
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="mk-shell-top sticky top-0 z-40 border-b border-thborder/10 bg-appbg/80 backdrop-blur-xl">
          <div className="flex h-16 items-center justify-between gap-3 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3">
              <button data-testid="mobile-menu-btn" onClick={() => setDrawer(true)} className="text-thmute md:hidden" aria-label="Open menu"><Menu className="h-5 w-5" /></button>
              {back && pathname !== "/" ? (
                <Link to="/" data-testid="module-back-link" className="inline-flex items-center gap-2 text-sm font-medium text-thmute transition-colors hover:text-thcyan">
                  <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">Back to hub</span>
                </Link>
              ) : (
                <span className="hidden font-mono-hud text-[10px] uppercase tracking-[0.3em] text-thdim sm:inline">見える化 · making visible</span>
              )}
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <TenantPill />
              <button data-testid="header-theme-toggle" onClick={toggle} aria-label="Toggle theme"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-thborder/12 text-thmute transition-colors hover:border-thcyan/50 hover:text-thcyan">
                {light ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
              </button>
              {isAdmin && (
                <Link to="/settings" data-testid="header-settings-link" className="mk-pill hidden md:inline-flex"><Settings className="h-3.5 w-3.5" /> Settings</Link>
              )}
              <div className="flex items-center gap-2.5 border-l border-thborder/10 pl-2 sm:pl-3">
                <span className="hidden sm:block"><Avatar name={user?.name} /></span>
                <div className="hidden lg:block">
                  <p data-testid="header-user-name" className="text-sm font-semibold leading-tight text-thstrong">{user?.name}</p>
                  <p className="mk-sub">{ROLE_LABEL[user?.role]}</p>
                </div>
                <button data-testid="header-signout-btn" onClick={() => { logout(); navigate("/login"); }} aria-label="Sign out"
                  className="ml-1 text-thmute transition-colors hover:text-thcoral"><LogOut className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        </header>
        <main className={`mx-auto w-full flex-1 px-4 py-6 sm:px-6 lg:px-8 ${wide ? "max-w-[1500px]" : "max-w-[1400px]"}`}>{children}</main>
      </div>
    </div>
  );
};
